import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectedAccessToken } from '../redux/reducers/accessTokenSlice';
import { selectUser } from '../redux/reducers/userSlice';
import { useMutation } from 'react-relay';
import { getAssessmentStatus } from '../graphQLOperations/SkillResource';
import { SkillResourceGetAssessmentStatusMutation } from '../graphQLOperations/__generated__/SkillResourceGetAssessmentStatusMutation.graphql';
import { useAuth0 } from '@auth0/auth0-react';
import { getIncompleteStatus } from '../bffApiCalls/getIncompleteStatus';
import { usePostHog } from '@posthog/react';

export type AssessmentState = 'initial' | 'review' | 'resume';

export interface DataLrnSessionBySkill {
  sessionId: string;
  state: AssessmentState;
  activityId: string;
  showIncorrectAnswers: boolean;
}

export interface DataLrnSession {
  sessionId: string;
  status: 'Incomplete' | 'new' | 'previous';
  previousActivityId?: string;
  count?: number;
}

export const useLrnSessionBySkill = (skillId: string, skipRequest: boolean) => {
  const learnerId = useAppSelector(selectUser)?.sub;
  // A refreshed token belongs to the same learner. Refetching on it would remount a running challenge.
  const hasAccessToken = Boolean(useAppSelector(selectedAccessToken));
  const key = `${learnerId ?? ''}|${skillId}`;
  const [lastSession, setLastSession] = useState<string | null>(null);
  const [stored, setStored] = useState<{ key: string; data: DataLrnSessionBySkill } | null>(null);
  const [refetch, setRefetch] = useState(false);
  const requestId = useRef(0);
  const [getAssessmentStatusMutation] = useMutation<SkillResourceGetAssessmentStatusMutation>(getAssessmentStatus);
  const data = stored?.key === key ? stored.data : null;
  const setData = (next: DataLrnSessionBySkill) => setStored({ key, data: next });

  const setAssessmentState = (state: AssessmentState) => {
    if (data) {
      const newData = { ...data, state, showIncorrectAnswers: true };
      setData(newData);
    }
  };

  const loadPreviousSesssion = (
    prevSessionId: string,
    previousActivityId: string,
    forceShowCorrectAnswers?: boolean,
  ) => {
    if (forceShowCorrectAnswers) {
      setLastSession(prevSessionId);
    }
    setData({
      sessionId: prevSessionId,
      state: 'review',
      activityId: previousActivityId,
      showIncorrectAnswers: forceShowCorrectAnswers || lastSession === prevSessionId,
    });
  };

  const refetchLrnSession = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    const id = ++requestId.current;
    if (!skipRequest) {
      getAssessmentStatusMutation({
        variables: { input: { skillId: skillId } },
        onCompleted: data => {
          if (id !== requestId.current) return;
          const payload = data.getStudentLearnositySession.incompleteLearnositySessionNode;
          setStored({
            key,
            data: {
              activityId: payload?.activityId || '',
              sessionId: payload?.sessionId,
              state: payload?.status === 'New' ? 'initial' : 'resume',
              showIncorrectAnswers: false,
            },
          });
          setLastSession(data.getStudentLearnositySession.incompleteLearnositySessionNode?.sessionId);
        },
        onError: err => {
          if (id !== requestId.current) return;
          console.log(err);
        },
      });
    }

    // A refetch, a skill or learner change, logout, skipping, or unmounting supersedes this lookup.
    return () => {
      requestId.current += 1;
      setLastSession(null);
      setStored(null);
    };
  }, [refetch, hasAccessToken, skipRequest, skillId, learnerId]);
  return { data, refetchLrnSession, loadPreviousSesssion, setAssessmentState };
};

export const useLrnSession = (
  activityId: string,
  userId: string,
  skipRequest: boolean,
  schoolYearStartDate?: string,
) => {
  const key = `${userId}|${activityId}|${schoolYearStartDate ?? ''}`;
  const [stored, setStored] = useState<{ key: string; data: DataLrnSession } | null>(null);
  const [pending, setPending] = useState<{ id: number; key: string } | null>(null);
  const [failure, setFailure] = useState<{ id: number; key: string; required: boolean } | null>(null);
  const requiredLookup = useRef({ key, required: true });
  const requestId = useRef(0);
  const accessTokenRedux = useAppSelector(selectedAccessToken);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const latest = useRef({ key, activityId, userId, schoolYearStartDate, getAccessTokenSilently, logout, posthog });
  useLayoutEffect(() => {
    // An abandoned render must not supersede the visible assessment's lookup.
    latest.current = { key, activityId, userId, schoolYearStartDate, getAccessTokenSilently, logout, posthog };
  });

  // Look up the current learner and activity; only the latest lookup for them may publish its session.
  const lookUp = (explicit = false) => {
    const id = ++requestId.current;
    const context = latest.current;
    if (requiredLookup.current.key !== context.key) requiredLookup.current = { key: context.key, required: true };
    if (explicit) requiredLookup.current.required = true;
    // A token refresh superseding Continue still needs fresh data before startup.
    const required = requiredLookup.current.required;
    setPending({ id, key: context.key });
    setFailure(null);
    return getIncompleteStatus(
      context.activityId,
      context.userId,
      context.getAccessTokenSilently,
      context.logout,
      context.posthog,
      context.schoolYearStartDate,
    )
      .then(responseData => {
        if (id === requestId.current && context.key === latest.current.key) {
          setStored({ key: context.key, data: responseData });
          requiredLookup.current.required = false;
        }
      })
      .catch(error => {
        if (id === requestId.current && context.key === latest.current.key)
          setFailure({ id, key: context.key, required });
        throw error;
      })
      .finally(() => setPending(current => (current?.id === id ? null : current)));
  };

  const refetchLrnSession = async () => {
    try {
      await lookUp(true);
    } catch (err) {
      console.error('Error getting incomplete-status', err);
    }
  };

  useEffect(() => {
    if (!skipRequest) {
      lookUp().catch(err => {
        console.error('Error getting incomplete-status', err);
      });
    }
    // A context or token change, logout, skipping, or unmounting supersedes this lookup.
    return () => {
      requestId.current += 1;
      setPending(null);
    };
  }, [accessTokenRedux, skipRequest, schoolYearStartDate, activityId, userId]);
  const lookupFailed = failure?.key === key && failure.id === requestId.current;
  return {
    data: stored?.key === key ? stored.data : null,
    // A lookup for the current learner and activity is in flight, so `data` may be about to change.
    refreshing: pending?.key === key,
    lookupFailed,
    requiredLookupFailed: lookupFailed && failure.required,
    refetchLrnSession,
  };
};
